/**
 * Normalizes chart data by removing problematic characters that can break chart rendering
 * @param chartContent - The raw chart content from the API
 * @returns Normalized chart content suitable for rendering
 */
export const normalizeChartData = (chartContent: string): string => {
  if (!chartContent) return chartContent;
  
  // Start with the original content
  let normalized = chartContent.trim();
  
  // If the content is on multiple lines (has actual newlines), keep the structure
  // but clean up any problematic characters
  if (normalized.includes('\n')) {
    // Handle actual newline characters - keep them for multi-line format
    // Remove any carriage returns
    normalized = normalized.replace(/\r/g, '');
    
    // Clean up each line individually
    const lines = normalized.split('\n');
    const cleanedLines = lines.map(line => {
      // Remove escaped quotes and backslashes that might come from JSON
      let cleanLine = line.replace(/\\"/g, '"');
      cleanLine = cleanLine.replace(/\\\\/g, '\\');
      // Trim whitespace but preserve line structure
      return cleanLine.trim();
    }).filter(line => line.length > 0); // Remove empty lines
    
    normalized = cleanedLines.join('\n');
  } else {
    // Single line format - convert to proper multi-line format for better parsing
    // Handle literal newline characters (actual \n in the string)
    normalized = normalized.replace(/\\n/g, '\n');
    
    // Handle literal carriage return characters (actual \r in the string)
    normalized = normalized.replace(/\\r/g, '');
    
    // Convert literal \" to regular quotes
    normalized = normalized.replace(/\\"/g, '"');
    
    // Convert literal \\ to single backslash
    normalized = normalized.replace(/\\\\/g, '\\');
    
    // If we now have newlines after processing, clean up the multi-line format
    if (normalized.includes('\n')) {
      const lines = normalized.split('\n');
      const cleanedLines = lines.map(line => line.trim()).filter(line => line.length > 0);
      normalized = cleanedLines.join('\n');
    } else {
      // Still single line - need to convert to proper multi-line xychart format
      // This is the key fix for the single-line issue
      normalized = convertSingleLineToMultiLine(normalized);
    }
  }
  
  // Final cleanup - remove any trailing/leading whitespace
  normalized = normalized.trim();
  
  return normalized;
};

/**
 * Converts single-line xychart format to proper multi-line format
 * @param singleLine - Single line chart content
 * @returns Multi-line formatted chart content
 */
function convertSingleLineToMultiLine(singleLine: string): string {
  // Handle the specific format from the API response
  let result = singleLine;
  
  // Add newlines after key xychart elements for proper parsing
  result = result.replace(/(xychart-beta|xychart)\s+/, '$1\n');
  result = result.replace(/title\s+"([^"]+)"\s+/, 'title "$1"\n');
  result = result.replace(/x-axis\s+(\[[^\]]+\])\s+/, 'x-axis $1\n');
  
  // Handle y-axis with optional range specification
  result = result.replace(/y-axis\s+"([^"]+)"\s+(\d+\s*-->\s*\d+\s+)?/, (match, label, range) => {
    return range ? `y-axis "${label}"\n` : `y-axis "${label}"\n`;
  });
  
  result = result.replace(/bar\s+(\[[^\]]+\])/, 'bar $1');
  
  // Clean up any multiple consecutive spaces that might have been created
  const lines = result.split('\n');
  const cleanedLines = lines.map(line => line.trim()).filter(line => line.length > 0);
  
  return cleanedLines.join('\n');
}

/**
 * Tests if the normalization function works correctly
 * This function is for development/testing purposes only
 */
export const testNormalization = () => {
  const testCases = [
    // Working case (no newlines)
    'xychart-beta title "Cloud Certificates and Number of People" x-axis ["AZ-900", "AZ-204", "AWS Solutions Architect", "Google Cloud Professional", "Certified Kubernetes Administrator"] y-axis "Number of People" bar [746]',
    
    // Problematic case (with newlines)
    'xychart-beta\ntitle "Cloud Certificates and Number of People"\nx-axis ["AZ-900", "AWS Solutions Architect", "Google Cloud Certified"]\ny-axis "Number of People"\nbar [434, 0, 0]',
    
    // Case with carriage returns
    'xychart-beta\r\ntitle "Test Chart"\r\nx-axis ["A", "B"]\r\ny-axis "Values"\r\nbar [1, 2]',
    
    // Case with mixed whitespace
    'xychart-beta   title   "Test"   x-axis   ["A"]   y-axis   "Values"   bar   [1]',
    
    // The exact case from the API response
    'xychart-beta\ntitle "Cloud Certificates and Number of Skilled People"\nx-axis ["AZ-900", "AZ-204", "AWS Solutions Architect"]\ny-axis "Number of People"\nbar [74, 6, 0]',
    
    // The problematic single-line case
    'xychart-beta title "Programming Languages and Number of Skilled People" x-axis [".NET", "Java", "Python", "JavaScript", "C#", "Ruby", "PHP"] y-axis "Number of People" 0 --> 100 bar [59, 87, 98, 93, 95, 4, 31]'
  ];

  console.log('Testing chart data normalization:');
  testCases.forEach((testCase, index) => {
    const normalized = normalizeChartData(testCase);
    console.log(`Test ${index + 1}:`);
    console.log(`Original: ${JSON.stringify(testCase)}`);
    console.log(`Normalized: ${JSON.stringify(normalized)}`);
    console.log('---');
  });
};
